import type { ActionItem } from "@/components/admin-dashboard/ActionItemsCard";
import type { DashboardRange } from "@/lib/dashboard-analytics";

export interface SigningSeriesPoint {
  month: string;
  mra: number;
  termsheet: number;
}

export interface BuybackSeriesPoint {
  month: string;
  amount: number;
  schedules: number;
}

export interface CashflowSeriesPoint {
  month: string;
  amount: number;
}

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const seriesRoot = (payload: unknown): Record<string, unknown> => {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;
  return root && typeof root === "object" ? (root as Record<string, unknown>) : {};
};

export function normalizeSigningTrend(
  payload: unknown
): SigningSeriesPoint[] {
  const body = seriesRoot(payload);
  const series = Array.isArray(body.series) ? body.series : [];
  return series.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      mra: Math.round(num(r.mra)),
      termsheet: Math.round(num(r.termsheet)),
    };
  });
}

export function normalizeBuybackTrend(payload: unknown): {
  series: BuybackSeriesPoint[];
  upcomingAmount: number;
} {
  const body = seriesRoot(payload);
  const seriesRaw = Array.isArray(body.series) ? body.series : [];
  const series = seriesRaw.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      amount: Math.round(num(r.amount)),
      schedules: Math.round(num(r.schedules)),
    };
  });
  return {
    series,
    upcomingAmount: Math.round(num(body.upcomingAmount ?? body.upcoming_amount)),
  };
}

export function normalizeCashflowTrend(payload: unknown): CashflowSeriesPoint[] {
  const body = seriesRoot(payload);
  const series = Array.isArray(body.series) ? body.series : [];
  return series.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      amount: Math.round(num(r.amount)),
    };
  });
}

export function normalizeActionItems(payload: unknown): ActionItem[] {
  const body = seriesRoot(payload);
  const items = Array.isArray(body.items) ? body.items : [];
  return items
    .map((row) => {
      const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
      const type = String(r.type ?? "") as ActionItem["type"];
      const priorityRaw = String(r.priority ?? "medium");
      const priority =
        priorityRaw === "high" || priorityRaw === "low" ? priorityRaw : "medium";
      return {
        id: String(r.id ?? type),
        type,
        title: String(r.title ?? ""),
        description: String(r.description ?? ""),
        priority,
        count: Math.round(num(r.count)),
        link: typeof r.link === "string" ? r.link : "/post-disbursement",
      } satisfies ActionItem;
    })
    .filter((item) => item.title && item.count > 0);
}

export type { DashboardRange };
